import React, {useContext, useEffect, useState} from 'react';
import { Dropdown } from '../dropdown';
import { Button } from '../button';
import { Emoticons } from '../forms';
import { Reaction } from '../thumb';
import { IAnswerProps, IComment } from './feedback-answers';
import { FeedbackCommentThread } from './feedback-comment-thread';
import { TeamResultsStore } from '../../../app/src/dashboard/team-results-store';
import {translateComment} from '../../../app/src/questions/questionUtil';
import {StoreContext} from '../../../app/src/base/root-store';

interface FeedbackAnswerItemProps {
    answer: IAnswerProps;
    store: TeamResultsStore;
    onCommentAdded?: (answerId: string, comment: IComment) => void;
    onLikeToggled?: (answerId: string, newLikeCount: number) => void;
    employeeId?: string;
    includeLikesAndComments?: boolean;
}

export const FeedbackAnswerItem = ({
    answer,
    store,
    onCommentAdded,
    onLikeToggled,
    employeeId,
    includeLikesAndComments,
}: FeedbackAnswerItemProps): JSX.Element => {
    const answerId = answer.id!;
    const [likes, setLikes] = useState(answer.likes ?? { likeCount: 0, likedEmployeeIds: [] });
    const [comments, setComments] = useState<IComment[]>(answer.comments ?? []);
    const [commentCount, setCommentCount] = useState(answer.commentCount ?? 0);
    const [openCommentId, setOpenCommentId] = useState<string | null>(null);
    const userStore = useContext(StoreContext).userStore;

    const isLiked = likes.likedEmployeeIds.includes(employeeId ?? '');

    useEffect(() => {
        if (!includeLikesAndComments) {
            return;
        }

        const load = async () => {
            const [likeData, loadedComments] = await Promise.all([
                store.loadLikeData(answerId),
                store.loadRepliesForQuestion(answerId),
            ]);

            store.variables.questionWithComment = {
                ...answer,
                likes: likeData ?? { likeCount: 0, likedEmployeeIds: [] },
                comments: loadedComments ?? [],
            };

            setLikes(likeData ?? { likeCount: 0, likedEmployeeIds: [] });
            setComments(loadedComments ?? []);
            setCommentCount(loadedComments?.length ?? 0);
        };

        load();
    }, [answerId, includeLikesAndComments]);

    const handleLikeToggle = async (e?: React.MouseEvent): Promise<void> => {
        if (!includeLikesAndComments) {
            return;
        }

        e?.preventDefault();
        e?.stopPropagation();

        try {
            const updatedLikes = await store.toggleLike(answerId);

            if (updatedLikes) {
                setLikes(updatedLikes);
                onLikeToggled?.(answerId, updatedLikes.likeCount);
            }
        } catch (err) {
            console.error('Like toggle failed:', err);
        }
    };

    const handleCommentToggle = (id: string) => {
        const isOpening = openCommentId !== id;

        if (isOpening) {
            store.variables.questionWithComment = answer;
        }
        setOpenCommentId(isOpening ? id : null);
    };

    const renderCommentThread = (): JSX.Element | null => {
        if (!includeLikesAndComments || openCommentId !== answerId) {
            return null;
        }

        return (
            <div className="comment-thread">
                <FeedbackCommentThread
                    answerId={answerId}
                    comments={comments}
                    store={store}
                    onCommentAdded={(newComment) => {
                        setComments((prev) => [...prev, newComment]);
                        setCommentCount((prev) => prev + 1);
                        onCommentAdded?.(answerId, newComment);
                    }}
                    onCommentDeleted={(deletedId) => {
                        const updated = comments.filter((c) => c.id !== deletedId);

                        setComments(updated);
                        setCommentCount(updated.length);
                    }}
                />
            </div>
        );
    };

    return (
        <li className="feedback-answers__answer">
            <div className="feedback-answers__answer-inner">
                <div className="feedback-answers__column feedback-answers__column--thumb">
                    {answer.reactionType === 'talk-bubble' ? (
                        <Reaction type="talk-bubble" />
                    ) : (
                        <Reaction direction={answer.direction} />
                    )}
                </div>

                <div className="feedback-answers__column">
                    {answer.time && (
                        <time className="feedback-answers__time" dateTime={answer.time.iso}>
                            {answer.time.value}
                        </time>
                    )}

                    <div className="feedback-answers__text-row">
                        {answer.commentToolTip ? (
                            <Dropdown
                                trigger={{
                                    children: (
                                        <Button
                                            text={
                                                store.translateCommentsEnabled
                                                    ? translateComment(answer, userStore.payload?.language)
                                                    : answer.text
                                            }
                                            showEmoticons
                                            className="button--style-link"
                                        />
                                    ),
                                }}
                            >
                                {answer.commentToolTip}
                            </Dropdown>
                        ) : (
                            <p className="feedback-answers__text">
                                <Emoticons text={
                                    store.translateCommentsEnabled
                                        ? translateComment(answer, userStore.payload?.language)
                                        : answer.text
                                } />
                            </p>
                        )}

                        {!includeLikesAndComments && answer.hasSeen !== undefined && (
                            <Dropdown
                                delayShow={200}
                                offset={[-10, 17]}
                                trigger={{
                                    children: (
                                        <Button
                                            text=""
                                            onClick={
                                                answer.onHasSeenCheck
                                                    ? () => answer.onHasSeenCheck?.(!answer.hasSeen)
                                                    : undefined
                                            }
                                            className="feedback-answers__favorite-button feedback-answers__favorite-button--inline"
                                            color="white"
                                            icon={answer.hasSeen ? 'check-green' : 'check-red'}
                                        />
                                    ),
                                }}
                            >
                                {answer.titleTooltip}
                            </Dropdown>
                        )}
                    </div>

                    <div className="feedback-answers__actions-wrapper">
                        <div className="feedback-answers__reactions-bar feedback-answers__reactions-bar--desktop-bottom">
                            {includeLikesAndComments && (
                                <>
                                    <Button
                                        icon="like"
                                        count={{
                                            number: likes.likeCount,
                                            color: isLiked ? 'green' : 'grey',
                                            size: 'sm',
                                        }}
                                        onClick={handleLikeToggle}
                                        style="reaction"
                                        disabled={!answerId}
                                    />
                                    <Button
                                        icon="message"
                                        onClick={() => handleCommentToggle(answerId)}
                                        count={{
                                            number: commentCount ?? 0,
                                            color: commentCount > 0 ? 'green' : 'grey',
                                            size: 'sm',
                                        }}
                                        style="reaction"
                                        disabled={!answerId}
                                    />
                                </>
                            )}

                            {includeLikesAndComments && answer.hasSeen !== undefined && (
                                <Dropdown
                                    delayShow={200}
                                    offset={[-10, 17]}
                                    trigger={{
                                        children: (
                                            <Button
                                                icon={answer.hasSeen ? 'check-green' : 'check-red'}
                                                onClick={() => answer.onHasSeenCheck?.(!answer.hasSeen)}
                                                className="feedback-answers__favorite-button"
                                                color="white"
                                                style="reaction"
                                            />
                                        ),
                                    }}
                                >
                                    {answer.titleTooltip}
                                </Dropdown>
                            )}
                        </div>
                    </div>

                    {renderCommentThread()}
                </div>
            </div>
        </li>
    );
};
